import React, { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { Button, InputNumber, Popover, Space } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import styled from 'styled-components';

import { KpiCard } from 'molecules';
import { useToasts } from 'core/hooks';
import { GlobalToastTypeNames, ToastTypes } from 'types';
import {
  usePaymentCalendarQuery,
  usePaymentCalendarSettingsQuery,
  useSetCashBalanceMutation,
} from 'core';

import { fmtMoney, toneColor } from '../shared';

export interface IBalanceNowCardProps {
  /** Refetch the page's main calendar query after the balance is corrected. */
  onChanged?: () => void;
}

const DATE_FMT = 'YYYY-MM-DD';

/**
 * «На счёте сейчас» — the first KPI card. The value is TODAY's end-of-day
 * balance from a dedicated one-day calendar query (so it never shifts with the
 * page horizon); the sublabel shows the date of the manual cash snapshot the
 * forecast is anchored to. The pencil opens an inline corrector that stores a
 * new snapshot for today (same semantics as the settings drawer) and refreshes
 * both this card and the main calendar.
 */
export const BalanceNowCard = memo<IBalanceNowCardProps>(({ onChanged }) => {
  const { t } = useTranslation();
  const { addToast } = useToasts();
  const today = moment().format(DATE_FMT);

  const { data, refetch } = usePaymentCalendarQuery({
    variables: { dateFrom: today, dateTo: today },
    fetchPolicy: 'cache-and-network',
  });
  const {
    data: settingsData,
    refetch: refetchSettings,
  } = usePaymentCalendarSettingsQuery({ fetchPolicy: 'cache-and-network' });
  const [setBalance, { loading: saving }] = useSetCashBalanceMutation();

  const [editOpen, setEditOpen] = useState(false);
  const [draft, setDraft] = useState<number | null>(null);

  const todayDay = data?.paymentCalendar?.days?.find((d) => d?.date === today);
  const balance = todayDay?.endBalancePln;

  // cashBalances is sorted by -as_of_date → [0] is the anchoring snapshot.
  const snapshot = settingsData?.cashBalances?.[0];

  const onSave = useCallback(async () => {
    if (draft === null || draft === undefined) return;
    const res = await setBalance({
      variables: { amountPln: draft, asOfDate: today },
    });
    const payload = res.data?.setCashBalance;
    addToast({
      data: {
        type: payload?.success ? ToastTypes.success : ToastTypes.error,
        title: payload?.success
          ? t('pages.paymentCalendar.toasts.saved')
          : t('pages.paymentCalendar.toasts.saveFailed'),
        text: payload?.success
          ? undefined
          : (payload?.errors || []).filter(Boolean).join('\n') || undefined,
      },
      type: GlobalToastTypeNames.PaymentCalendar,
    });
    if (payload?.success) {
      setEditOpen(false);
      setDraft(null);
      refetch();
      refetchSettings();
      onChanged?.();
    }
  }, [
    draft,
    setBalance,
    today,
    addToast,
    t,
    refetch,
    refetchSettings,
    onChanged,
  ]);

  const editor = (
    <Space direction="vertical" size="small">
      <InputNumber
        autoFocus
        step={0.01}
        style={{ width: 180 }}
        value={draft ?? undefined}
        onChange={(v) => setDraft(typeof v === 'number' ? v : null)}
      />
      <Button
        type="primary"
        size="small"
        block
        loading={saving}
        disabled={draft === null}
        onClick={onSave}
      >
        {t('pages.paymentCalendar.balanceNow.save')}
      </Button>
    </Space>
  );

  return (
    <KpiCard
      label={t('pages.paymentCalendar.balanceNow.title')}
      accent={
        balance !== null && balance !== undefined
          ? toneColor(balance)
          : undefined
      }
      value={
        <ValueRow>
          <span>
            {balance !== null && balance !== undefined
              ? fmtMoney(balance)
              : '—'}
          </span>
          <Popover
            trigger="click"
            visible={editOpen}
            onVisibleChange={(open) => {
              setEditOpen(open);
              if (open) setDraft(balance ?? null);
            }}
            title={t('pages.paymentCalendar.balanceNow.edit')}
            content={editor}
          >
            <EditButton
              type="button"
              aria-label={t('pages.paymentCalendar.balanceNow.edit')}
            >
              <EditOutlined />
            </EditButton>
          </Popover>
        </ValueRow>
      }
      subLabel={
        snapshot?.asOfDate
          ? t('pages.paymentCalendar.balanceNow.asOfSnapshot', {
              date: moment(snapshot.asOfDate).format('DD.MM.YYYY'),
            })
          : t('pages.paymentCalendar.balanceNow.notSet')
      }
    />
  );
});

/* ----------------------------- styles ----------------------------- */

const ValueRow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const EditButton = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  color: #94a3b8;
  font-size: 14px;
  line-height: 1;

  &:hover {
    color: #2563eb;
  }
`;
